import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errorMessage } from "@/lib/api";
import { formatDate, todayISO } from "@/lib/services";

const PRESETS = [7, 15, 30, 60, 90, 365];

const addDays = (iso, n) => {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

export const GrantDialog = ({ company, onClose, onDone }) => {
  const [days, setDays] = useState("30");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (company) setDays("30"); }, [company]);

  const n = Number(days);
  const valid = Number.isInteger(n) && n >= 1 && n <= 3650;
  const today = todayISO();
  const base = company && company.billing.paid_until > today ? company.billing.paid_until : today;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/platform/companies/${company.id}/grant`, { days: n });
      toast.success(`${company.company_name} liberada até ${formatDate(data.billing.paid_until)}.`);
      onDone();
      onClose();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!company} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-slate-700 bg-slate-900 sm:max-w-md" data-testid="grant-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-bold">Liberar acesso</DialogTitle>
          <DialogDescription>{company?.company_name}. Os dias somam ao prazo atual, com ou sem pagamento. Ao acabar, a empresa é bloqueada se não pagar.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {PRESETS.map((p) => (
              <button key={p} type="button" onClick={() => setDays(String(p))} data-testid={`grant-preset-${p}`}
                className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors duration-150 ${String(p) === days ? "border-slate-100 bg-slate-100 text-slate-900" : "border-slate-700 text-slate-300"}`}>{p} dias</button>
            ))}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="grant_days">Quantidade de dias</Label>
            <Input id="grant_days" type="number" min={1} max={3650} value={days} onChange={(e) => setDays(e.target.value)} className="h-10 bg-slate-950" data-testid="grant-days-input" />
          </div>
          <p className="rounded-lg border border-slate-700 bg-slate-950/50 p-3 text-sm" data-testid="grant-preview">
            Acesso atual até <b>{formatDate(company?.billing.paid_until)}</b> → novo prazo <b className="text-emerald-300">{valid ? formatDate(addDays(base, n)) : "—"}</b>
          </p>
          <DialogFooter className="gap-2">
            <Button type="button" variant="ghost" onClick={onClose} data-testid="grant-cancel">Cancelar</Button>
            <Button type="submit" disabled={busy || !valid} className="rounded-full font-semibold" data-testid="grant-submit">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Liberar {valid ? `${n} dias` : ""}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
